import { useEffect, useState } from "react";
import { apiFetch } from "../api";
import { BASE_URL } from "../../commonUrls";
import { useApiCall } from "../useApiCall";
import "./WeeklyReviewPage.css";

export default function WeeklyReviewPage() {
  const { execute, loading, error } = useApiCall();
  const [review, setReview] = useState(null);

  useEffect(() => {
    execute(() =>
      apiFetch(`${BASE_URL}/review/weekly`).then((r) => (r.ok ? r.json() : null))
    ).then((d) => { if (d) setReview(d); });
  }, []);

  if (loading) return <div className="wr-loading">Loading your weekly review...</div>;
  if (error)   return <div className="wr-error">{error}</div>;
  if (!review) return null;

  const plannerRate = review.plannerCreated > 0
    ? Math.round((review.plannerDone / review.plannerCreated) * 100) : 0;

  const habitRate = review.habitsActive > 0
    ? Math.round((review.habitCheckInsThisWeek / (review.habitsActive * 7)) * 100) : 0;

  return (
    <div className="wr-page">
      <h2 className="wr-title">Weekly Review</h2>
      <p className="wr-subtitle">
        {review.weekStart} → {review.weekEnd}
      </p>

      <div className="wr-grid">
        {/* Planner */}
        <section className="wr-card">
          <h3 className="wr-card-title">Planner</h3>
          <div className="wr-stats-row">
            <Stat value={review.plannerCreated} label="Created" />
            <Stat value={review.plannerDone}    label="Done" accent="green" />
            <Stat value={review.plannerSkipped} label="Skipped" accent="orange" />
            <Stat value={plannerRate + "%"}      label="Completion" accent="blue" />
          </div>
          {review.completedTitles?.length > 0 && (
            <ul className="wr-done-list">
              {review.completedTitles.map((t, i) => (
                <li key={i} className="wr-done-item">✓ {t}</li>
              ))}
            </ul>
          )}
        </section>

        {/* Habits */}
        <section className="wr-card">
          <h3 className="wr-card-title">Habits</h3>
          <div className="wr-stats-row">
            <Stat value={review.habitsActive}        label="Active" />
            <Stat value={review.habitCheckInsThisWeek} label="Check-ins" accent="green" />
            <Stat value={review.habitsPerfectWeek}   label="Perfect week" accent="blue" />
            <Stat value={habitRate + "%"}             label="Consistency" accent={habitRate >= 70 ? "green" : "orange"} />
          </div>
          {review.habitStats?.length > 0 && (
            <table className="wr-habit-table">
              <thead>
                <tr><th>Habit</th><th>Days done</th></tr>
              </thead>
              <tbody>
                {review.habitStats.map((h, i) => (
                  <tr key={i}>
                    <td>{h.name}</td>
                    <td>
                      <HeatBar done={h.completedDays} total={7} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Tasks */}
        <section className="wr-card">
          <h3 className="wr-card-title">Assigned Tasks</h3>
          <div className="wr-stats-row">
            <Stat value={review.tasksClosedThisWeek} label="Closed this week" accent="green" />
            <Stat value={review.tasksStillOpen}       label="Still open"       accent={review.tasksStillOpen > 5 ? "red" : "blue"} />
          </div>
        </section>

        {/* Notes & Finance */}
        <section className="wr-card">
          <h3 className="wr-card-title">Notes & Finance</h3>
          <div className="wr-stats-row">
            <Stat value={review.notesCreatedThisWeek} label="Notes written" accent="blue" />
            {review.totalSpentThisWeek && (
              <Stat value={review.totalSpentThisWeek} label="Spent (month)" accent="orange" />
            )}
            {review.topSpendingCategory && (
              <Stat value={review.topSpendingCategory} label="Top category" />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Stat({ value, label, accent = "default" }) {
  return (
    <div className={`wr-stat wr-stat-${accent}`}>
      <div className="wr-stat-value">{value ?? "—"}</div>
      <div className="wr-stat-label">{label}</div>
    </div>
  );
}

function HeatBar({ done, total }) {
  return (
    <div className="wr-heat-bar">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`wr-heat-cell ${i < done ? "wr-heat-done" : ""}`} />
      ))}
    </div>
  );
}
